import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorGroup, FrontendErrorsCategory } from "@/features/observability/types/frontend-errors.types";

export type CommandCenterErrorsWindow = {
    workspaceId: string;
    from: number;
    to: number;
};

export type CommandCenterErrorsRoute = { route: string; count: number };

export type CommandCenterErrorsDetail = {
    share: number | null;
    devices: number | null;
    perDevice: number | null;
    fingerprints: number;
    routes: CommandCenterErrorsRoute[];
    errorClasses: string[];
    httpStatuses: number[];
    message: string | null;
    firstSeenAt: number | null;
    lastSeenAt: number | null;
    releases: string[];
};

export type CommandCenterErrorsBar = {
    errorCode: string | null;
    label: string;
    count: number;
    category: FrontendErrorsCategory;
    detail: CommandCenterErrorsDetail;
};

export type CommandCenterErrorsAxis = { max: number; ticks: number[] };

// Design v2: 14 columns across the card, 7 when «Necesita atención» takes the right of the row.
const COLUMNS = { calm: 14, attention: 7 };
const TOP_ROUTES = 3;
// The mantissas × 10, so every candidate from 10 up is a whole count.
const NICE_TENTHS = [10, 12, 15, 20, 25, 30, 40, 50, 60, 80];
const AXIS_DIVISIONS = 4;
const SMALL_MAX = 8;

const niceMax = (largest: number): number => {
    for (let magnitude = 1; ; magnitude *= 10) {
        const found = NICE_TENTHS.map((tenths) => tenths * magnitude).find((candidate) => candidate >= largest);
        if (found !== undefined) return found;
    }
};

const MAX_RELEASES = 5;
const NO_CODE_LABEL = "sin código";

const CATEGORY_DESCRIPTIONS: Record<FrontendErrorsCategory, string> = {
    technical_fault: "Fallo técnico: una llamada o un proceso no terminó bien. Suele requerir acción del equipo.",
    business_rejection: "Rechazo de negocio: la operación se denegó por una regla (límite, KYC, bloqueo). No es un fallo del sistema.",
    user_cancelled: "Cancelado por el usuario: la operación se abandonó o se canceló a propósito. No es un fallo del sistema.",
    warning: "Aviso: algo anómalo que no impide continuar.",
    unknown: "Sin clasificar: el evento no trae una categoría reconocida.",
};

const dominantCategory = (counts: Map<FrontendErrorsCategory, number>): FrontendErrorsCategory => {
    let best: FrontendErrorsCategory = "unknown";
    let bestCount = -1;
    frontendErrorsFormat.CATEGORIES.forEach((key) => {
        const count = counts.get(key);
        if (count !== undefined && count > bestCount) {
            best = key;
            bestCount = count;
        }
    });
    return best;
};

type CodeTotal = {
    errorCode: string | null;
    count: number;
    byCategory: Map<FrontendErrorsCategory, number>;
    groups: FrontendErrorGroup[];
};

// Insertion order is kept, so the first (heaviest) group decides the order.
const distinct = <T,>(values: (T | null)[]): T[] => {
    const seen = new Set<T>();
    values.forEach((value) => {
        if (value !== null) seen.add(value);
    });
    return Array.from(seen);
};

const extreme = (values: (number | null)[], pick: (left: number, right: number) => number): number | null =>
    values.reduce<number | null>((best, value) => (value === null ? best : best === null ? value : pick(best, value)), null);

// A device can hit several fingerprints of one code, so the per-group cardinalities only add up to an approximation;
// one group that cannot say leaves the whole code unknown.
const devicesOf = (groups: FrontendErrorGroup[]): number | null =>
    groups.reduce<number | null>((sum, group) => (sum === null || group.affectedDevicesApprox === null ? null : sum + group.affectedDevicesApprox), 0);

const routesOf = (groups: FrontendErrorGroup[]): CommandCenterErrorsRoute[] => {
    const byRoute = new Map<string, number>();
    groups.forEach((group) => {
        if (group.route !== null) byRoute.set(group.route, (byRoute.get(group.route) ?? 0) + group.count);
    });
    return Array.from(byRoute.entries())
        .map(([route, count]) => ({ route, count }))
        .sort((left, right) => (right.count !== left.count ? right.count - left.count : left.route.localeCompare(right.route)))
        .slice(0, TOP_ROUTES);
};

const detailOf = (total: CodeTotal, allOccurrences: number): CommandCenterErrorsDetail => {
    const byCount = [...total.groups].sort((left, right) => right.count - left.count);
    const devices = devicesOf(byCount);
    const top = byCount[0];
    return {
        share: allOccurrences === 0 ? null : total.count / allOccurrences,
        devices,
        perDevice: devices === null || devices === 0 ? null : total.count / devices,
        fingerprints: distinct(byCount.map((group) => group.fingerprint)).length,
        routes: routesOf(byCount),
        errorClasses: distinct(byCount.map((group) => group.errorClass)),
        httpStatuses: distinct(byCount.map((group) => group.httpStatus)),
        message: top === undefined ? null : top.sampleMessage,
        firstSeenAt: extreme(byCount.map((group) => group.firstSeenAt), Math.min),
        lastSeenAt: extreme(byCount.map((group) => group.lastSeenAt), Math.max),
        releases: distinct(byCount.flatMap((group) => (group.releases === null ? [] : group.releases))).slice(0, MAX_RELEASES),
    };
};

const columnsFor = (attention: boolean): number => (attention ? COLUMNS.attention : COLUMNS.calm);

// The top tick is the smallest m × 10^k ≥ the largest count, m in 1-1.2-1.5-2-2.5-3-4-5-6-8, so the tallest column fills
// at least ~3/4 of the plot as in the design; the other ticks are its quarters, shown as whole counts. Up to 8, the top
// is 4 or 8 so every quarter is already whole.
const axis = (largest: number): CommandCenterErrorsAxis => {
    const max = largest <= SMALL_MAX ? (largest <= SMALL_MAX / 2 ? SMALL_MAX / 2 : SMALL_MAX) : niceMax(largest);
    return { max, ticks: Array.from({ length: AXIS_DIVISIONS + 1 }, (_, index) => Math.round((max * (AXIS_DIVISIONS - index)) / AXIS_DIVISIONS)) };
};

const bars = (groups: FrontendErrorGroup[], limit: number): CommandCenterErrorsBar[] => {
    const totals = new Map<string | null, CodeTotal>();
    groups.forEach((group) => {
        const current = totals.get(group.errorCode);
        const total: CodeTotal = current === undefined
            ? { errorCode: group.errorCode, count: 0, byCategory: new Map<FrontendErrorsCategory, number>(), groups: [] }
            : current;
        const category = frontendErrorsFormat.isCategory(group.category) ? group.category : "unknown";
        total.count += group.count;
        total.byCategory.set(category, (total.byCategory.get(category) ?? 0) + group.count);
        total.groups.push(group);
        totals.set(group.errorCode, total);
    });
    const allOccurrences = groups.reduce((sum, group) => sum + group.count, 0);
    const labelOf = (total: CodeTotal): string => (total.errorCode === null ? NO_CODE_LABEL : total.errorCode);
    // The detail is only built for the bars shown.
    return Array.from(totals.values())
        .sort((left, right) => (right.count !== left.count ? right.count - left.count : labelOf(left).localeCompare(labelOf(right))))
        .slice(0, limit)
        .map((total) => ({
            errorCode: total.errorCode,
            label: labelOf(total),
            count: total.count,
            category: dominantCategory(total.byCategory),
            detail: detailOf(total, allOccurrences),
        }));
};

const href = (range: CommandCenterErrorsWindow, errorCode: string | null): string => {
    const search = new URLSearchParams({
        workspaceId: range.workspaceId,
        source: "frontend",
        from: String(range.from),
        to: String(range.to),
    });
    if (errorCode !== null) search.set("errorCode", errorCode);
    return `/frontend-errors?${search.toString()}`;
};

const categoryDescription = (category: FrontendErrorsCategory): string => CATEGORY_DESCRIPTIONS[category];

export const commandCenterErrorsModel = {
    bars,
    columnsFor,
    axis,
    href,
    categoryDescription,
};
